import { useState, useEffect, useCallback } from 'react';
import { Settings, Key, Zap, AlertTriangle, CheckCircle2, Trash2, TestTube } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

interface StripeStatus {
  modo: 'simulado' | 'test' | 'live';
  temChaves: boolean;
  publishableKey: string | null;
}

const MODO_CONFIG: Record<string, { bg: string; color: string; icon: string; label: string }> = {
  simulado: { bg: '#dbeafe', color: '#1e40af', icon: '🔵', label: 'SIMULADO' },
  test: { bg: '#fef3c7', color: '#92400e', icon: '🟡', label: 'TESTE' },
  live: { bg: '#dcfce7', color: '#166534', icon: '🟢', label: 'PRODUÇÃO' },
};

export function StripeConfigPanel() {
  const [status, setStatus] = useState<StripeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [simResult, setSimResult] = useState<any>(null);
  const [form, setForm] = useState({ secretKey: '', publishableKey: '', webhookSecret: '' });

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/billing/config/status');
      setStatus(data);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSave = async () => {
    if (!form.secretKey && !form.publishableKey) {
      toast.error('Informe ao menos a Secret Key');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/billing/config/keys', form);
      toast.success(`Chaves salvas! Modo: ${data.modo}`);
      setShowForm(false);
      setForm({ secretKey: '', publishableKey: '', webhookSecret: '' });
      loadStatus();
    } catch { toast.error('Erro de conexão'); }
    setSaving(false);
  };

  const handleClear = async () => {
    if (!confirm('Remover todas as chaves Stripe? O sistema voltará ao modo simulado.')) return;
    try {
      await api.delete('/billing/config/keys');
      toast.success('Chaves removidas. Modo simulado ativo.');
      loadStatus();
      setTestResult(null);
    } catch { toast.error('Erro ao limpar'); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/billing/config/testar');
      setTestResult(data);
      if (data.ok) toast.success(data.mensagem);
      else toast.error(data.mensagem);
    } catch { toast.error('Erro ao testar'); }
    setTesting(false);
  };

  const handleSimulate = async () => {
    setSimulating(true);
    setSimResult(null);
    try {
      const { data } = await api.post('/billing/config/simular', {
        email: 'teste@municipio.gov.br',
        nome: 'Municipio de Teste',
      });
      setSimResult(data);
      toast.success(data.mensagem || 'Simulação concluída');
    } catch { toast.error('Erro na simulação'); }
    setSimulating(false);
  };

  if (loading) return null;

  const modoCfg = MODO_CONFIG[status?.modo || 'simulado'];

  return (
    <div style={{
      background: 'var(--bg-card, white)', borderRadius: 14,
      border: '1px solid var(--border-color, #e5e7eb)', padding: 20, marginTop: 20,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <Settings size={18} style={{ color: '#7c3aed' }} /> Configuração Stripe
        </h3>
        <span style={{
          padding: '4px 14px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
          background: modoCfg.bg, color: modoCfg.color, letterSpacing: '0.5px',
        }}>
          {modoCfg.icon} {modoCfg.label}
        </span>
      </div>

      {/* Status info */}
      <div style={{
        background: modoCfg.bg, borderRadius: 10, padding: '14px 18px', marginBottom: 16,
        border: `1px solid ${modoCfg.color}22`,
      }}>
        {status?.modo === 'simulado' ? (
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: modoCfg.color, marginBottom: 4 }}>
              Modo Simulado Ativo
            </div>
            <p style={{ fontSize: '0.78rem', color: modoCfg.color, opacity: 0.8, margin: 0 }}>
              Nenhuma chave configurada. Todas as operações de billing serão simuladas internamente
              com respostas compatíveis com o Stripe real. Ideal para desenvolvimento e demonstrações.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: modoCfg.color, marginBottom: 4 }}>
                Stripe Conectado — Modo {status?.modo === 'test' ? 'Teste' : 'Produção'}
              </div>
              {status?.publishableKey && (
                <p style={{ fontSize: '0.72rem', color: modoCfg.color, opacity: 0.7, margin: 0, fontFamily: 'monospace' }}>
                  Publishable: {status.publishableKey}
                </p>
              )}
            </div>
            <button onClick={handleClear} style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8,
              border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626',
              fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
            }}>
              <Trash2 size={12} /> Remover
            </button>
          </div>
        )}
      </div>

      {/* Ações */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <button onClick={() => setShowForm(!showForm)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10,
          border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, white)',
          fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', color: 'var(--text-main, #1e293b)',
        }}>
          <Key size={14} /> {showForm ? 'Fechar' : 'Configurar Chaves'}
        </button>

        <button onClick={handleTest} disabled={testing} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10,
          border: 'none', background: '#2563eb', color: 'white',
          fontSize: '0.78rem', fontWeight: 600, cursor: testing ? 'wait' : 'pointer',
          opacity: testing ? 0.7 : 1,
        }}>
          <Zap size={14} /> {testing ? 'Testando...' : 'Testar Conexão'}
        </button>

        <button onClick={handleSimulate} disabled={simulating} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10,
          border: 'none', background: '#7c3aed', color: 'white',
          fontSize: '0.78rem', fontWeight: 600, cursor: simulating ? 'wait' : 'pointer',
          opacity: simulating ? 0.7 : 1,
        }}>
          <TestTube size={14} /> {simulating ? 'Simulando...' : 'Simular Fluxo'}
        </button>
      </div>

      {/* Form de chaves */}
      {showForm && (
        <div style={{
          background: 'var(--bg-main, #f9fafb)', borderRadius: 10, padding: 18,
          border: '1px solid var(--border-color, #e5e7eb)', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Secret Key *
              </label>
              <input
                type="password"
                placeholder="sk_test_..."
                value={form.secretKey}
                onChange={e => setForm({ ...form, secretKey: e.target.value })}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)',
                  fontSize: '0.85rem', fontFamily: 'monospace',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Publishable Key
              </label>
              <input
                type="text"
                placeholder="pk_test_..."
                value={form.publishableKey}
                onChange={e => setForm({ ...form, publishableKey: e.target.value })}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)',
                  fontSize: '0.85rem', fontFamily: 'monospace',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
                Webhook Secret
              </label>
              <input
                type="password"
                placeholder="whsec_..."
                value={form.webhookSecret}
                onChange={e => setForm({ ...form, webhookSecret: e.target.value })}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: '1px solid var(--border-color, #e5e7eb)',
                  background: 'var(--bg-card, white)', color: 'var(--text-main, #1e293b)',
                  fontSize: '0.85rem', fontFamily: 'monospace',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)',
                background: 'var(--bg-card, white)', fontSize: '0.78rem', cursor: 'pointer',
              }}>
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving} style={{
                padding: '8px 20px', borderRadius: 8, border: 'none',
                background: '#2563eb', color: 'white', fontWeight: 700,
                fontSize: '0.78rem', cursor: saving ? 'wait' : 'pointer',
              }}>
                {saving ? 'Salvando...' : 'Salvar Chaves'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resultado do teste */}
      {testResult && (
        <div style={{
          background: testResult.ok ? '#f0fdf4' : '#fef2f2', borderRadius: 10,
          padding: '12px 16px', marginBottom: 12,
          border: `1px solid ${testResult.ok ? '#bbf7d0' : '#fecaca'}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {testResult.ok ? <CheckCircle2 size={16} color="#16a34a" /> : <AlertTriangle size={16} color="#dc2626" />}
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: testResult.ok ? '#166534' : '#991b1b' }}>
              {testResult.mensagem}
            </div>
            <div style={{ fontSize: '0.68rem', color: testResult.ok ? '#16a34a' : '#dc2626', textTransform: 'uppercase' }}>
              Modo: {testResult.modo}
            </div>
          </div>
        </div>
      )}

      {/* Resultado da simulação */}
      {simResult && (
        <div style={{
          background: '#f5f3ff', borderRadius: 10, padding: '14px 18px',
          border: '1px solid #e9d5ff',
        }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#6d28d9', marginBottom: 8 }}>
            Resultado da Simulação ({simResult.customer?.modo || 'simulado'})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: '0.75rem' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>Customer</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#6b7280' }}>
                ID: {simResult.customer?.id}<br />
                Email: {simResult.customer?.email}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>Subscription</div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#6b7280' }}>
                ID: {simResult.subscription?.id}<br />
                Status: {simResult.subscription?.status}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
